import { Icon } from '@iconify/react';
import Stack from '@mui/material/Stack';

import { DATA_GRID_ROW_TYPE, DataGrid, ExpandableText } from 'modules/shared/components';
import { capitalizeString, formatAmount, formatDateTime } from 'modules/shared/utils';
import { CANDIDATE_STATUS } from '../constants';
import { getCandidateStatusMeta } from '../utils';

/**
 * Displays structured, read-only details for a candidate.
 * Optionally renders an interview editor in place of interview datetime.
 *
 * @param {object} props
 * @param {object} props.candidate - Candidate data object
 * @param {string} props.status - Current candidate status
 * @param {React.ReactNode|null} props.interviewEditor - Optional interview editor UI
 */
export default function CandidateDetailsCard({ candidate, interviewEditor }: { candidate: any; interviewEditor?: any }) {
  const statusMeta = getCandidateStatusMeta(candidate);

  const interviewRow =
    statusMeta.status !== CANDIDATE_STATUS.REJECTED
      ? {
          label: 'Interview date & time',
          labelAlignment: 'center',
          value: interviewEditor ?? formatDateTime(candidate.nextInterview) ?? 'Not scheduled',
        }
      : null;

  const leftColumn = {
    rows: [
      { label: 'Candidate name', value: candidate.name },
      { label: 'Department', value: candidate.department },
      { label: 'Contact number', value: candidate.contact },
      {
        label: 'LinkedIn',
        render: () => (
          <a href={candidate.linkedInUrl} target='_blank' rel='noreferrer' className='underline'>
            {candidate.linkedInUrl}
          </a>
        ),
      },
      { label: 'Relevant experience', value: `${candidate.experience} years` },
      { label: 'Current salary', value: formatAmount(candidate.currentSalary) },
      { label: 'Language known', value: candidate.languages },
      {
        label: 'Skills and Attributes',
        render: () => (
          <ExpandableText text={candidate.skills} sx={{ '& > span': { wordBreak: 'break-all' } }} />
        ),
      },
      {
        label: 'Resume',
        render: () => (
          <a href={candidate.resumeUrl} target='_blank' rel='noreferrer' className='underline'>
            view
          </a>
        ),
      },
      {
        label: 'Additional file',
        render: () =>
          candidate.additionalFileUrl ? (
            <a
              href={candidate.additionalFileUrl}
              target='_blank'
              rel='noreferrer'
              className='underline'
            >
              view
            </a>
          ) : (
            <span className='unavailable-message'>Not provided</span>
          ),
      },
    ],
  };
  const rightColumn = {
    rows: [
      {
        type: DATA_GRID_ROW_TYPE.INLINE,
        items: [
          { label: 'Age', value: candidate.age },
          { label: 'Gender', value: capitalizeString(candidate.gender) },
        ],
      },
      { label: 'Designation', value: candidate.designation },
      { label: 'Email ID', value: candidate.email },
      { label: 'Qualification', value: candidate.qualification },
      { label: 'Current Employer', value: candidate.currentEmployer },
      { label: 'Expected Salary', value: formatAmount(candidate.expectedSalary) },
      {
        label: 'Tools known',
        render: () => (
          <Stack
            direction='row'
            sx={{ flexWrap: 'wrap', rowGap: 1, columnGap: 3, justifyContent: 'flex-end' }}
          >
            {candidate.tools?.map(({ name: toolName, status }) => (
              <span key={toolName} className='row'>
                {toolName}{' '}
                <Icon
                  icon={status ? 'iconamoon:check' : 'basil:cross-outline'}
                  width={status ? 16 : 24}
                />
              </span>
            ))}
          </Stack>
        ),
      },
      {
        label: 'Why you are the right fit for this job',
        render: () => <ExpandableText text={candidate.whyJoin} sx={{ wordBreak: 'break-all' }} />,
      },
      ...(interviewRow ? [interviewRow] : []),
    ],
  };

  return <DataGrid columns={[leftColumn, rightColumn]} />;
}
